/**
 * ModifyRoutesModal - Edit trip route/transport types
 * 
 * Features:
 * - List of route legs with transport type selector
 * - Origin city display
 * - Return leg toggle and transport type
 * - Save calls POST /api/trip/:tripId/route
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    TripContent, 
    Route, 
    TransportType, 
    RouteLeg,
    updateTripRoute 
} from '../../services/api';
import { TransportIcon, MapPinIcon } from '../icons/TransportIcons';
import './ModifyRoutesModal.css';

// =============================================================================
// TYPES
// =============================================================================

interface ModifyRoutesModalProps {
    isOpen: boolean;
    onClose: () => void;
    content: TripContent;
    tripId: string;
    editToken?: string;
    onRouteUpdated: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const TRANSPORT_OPTIONS: { type: TransportType; labelKey: string }[] = [
    { type: 'flight', labelKey: 'route.transport.flight' },
    { type: 'train', labelKey: 'route.transport.train' },
    { type: 'car', labelKey: 'route.transport.car' },
    { type: 'bus', labelKey: 'route.transport.bus' },
    { type: 'ferry', labelKey: 'route.transport.ferry' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export default function ModifyRoutesModal({
    isOpen,
    onClose,
    content,
    tripId,
    editToken,
    onRouteUpdated,
}: ModifyRoutesModalProps) {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Local route state for editing
    const [localRoute, setLocalRoute] = useState<Route>(() => initializeRoute(content));
    
    // Reset local state when modal opens
    useEffect(() => {
        if (isOpen) {
            setLocalRoute(initializeRoute(content));
            setError(null);
        }
    }, [isOpen, content]);
    
    // Initialize route from content or create default; normalize to match cities length
    function initializeRoute(content: TripContent): Route {
        const cityCount = content.cities?.length ?? 0;
        const defaultOrigin = {
            label: t('route.your_city', 'Your city'),
            source: 'unknown' as const,
        };
        const defaultReturn = {
            enabled: true,
            transport: { type: 'flight' as const },
            toOrigin: true,
        };

        if (content.route && content.route.legs) {
            const cloned = JSON.parse(JSON.stringify(content.route)) as Route;
            // Normalize legs: exactly one leg per city
            const legs: RouteLeg[] = [];
            for (let idx = 0; idx < cityCount; idx++) {
                const existing = cloned.legs[idx];
                const type = existing?.transport?.type && existing.transport.type !== 'unknown'
                    ? existing.transport.type
                    : 'flight';
                legs.push({
                    toCityIdx: idx,
                    transport: existing?.transport
                        ? { ...existing.transport, type }
                        : { type },
                });
            }
            return {
                origin: cloned.origin || defaultOrigin,
                legs,
                return: cloned.return ?? defaultReturn,
            };
        }

        const defaultLegs: RouteLeg[] = Array.from({ length: cityCount }, (_, idx) => ({
            toCityIdx: idx,
            transport: { type: 'flight' as const },
        }));

        return {
            origin: defaultOrigin,
            legs: defaultLegs,
            return: defaultReturn,
        };
    }
    
    // Update transport type for a leg
    const updateLegTransport = (legIndex: number, type: TransportType) => {
        setLocalRoute(prev => ({
            ...prev,
            legs: prev.legs.map((leg, idx) => 
                idx === legIndex 
                    ? { ...leg, transport: { ...leg.transport, type } }
                    : leg
            ),
        }));
    };
    
    // Update return transport type
    const updateReturnTransport = (type: TransportType) => {
        setLocalRoute(prev => ({
            ...prev,
            return: prev.return 
                ? { ...prev.return, transport: { ...prev.return.transport, type } }
                : { enabled: true, transport: { type }, toOrigin: true },
        }));
    };
    
    // Toggle return leg
    const toggleReturn = (enabled: boolean) => {
        setLocalRoute(prev => ({
            ...prev,
            return: prev.return 
                ? { ...prev.return, enabled }
                : { enabled, transport: { type: 'flight' }, toOrigin: true },
        }));
    };
    
    // Save route
    const handleSave = async () => {
        setIsSaving(true);
        setError(null);

        // Ensure we never send 'unknown' for transport type (backend accepts it but UI should prefer a concrete type)
        const routeToSave: Route = {
            ...localRoute,
            legs: localRoute.legs.map(leg => ({
                ...leg,
                transport: {
                    ...leg.transport,
                    type: leg.transport?.type === 'unknown' ? 'flight' : (leg.transport?.type ?? 'flight'),
                },
            })),
            return: localRoute.return
                ? {
                    ...localRoute.return,
                    transport: localRoute.return.transport
                        ? {
                            ...localRoute.return.transport,
                            type: localRoute.return.transport.type === 'unknown'
                                ? 'flight'
                                : localRoute.return.transport.type,
                        }
                        : { type: 'flight' },
                }
                : { enabled: true, transport: { type: 'flight' }, toOrigin: true },
        };

        try {
            await updateTripRoute(tripId, routeToSave, editToken);
            onRouteUpdated();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save route');
        } finally {
            setIsSaving(false);
        }
    };
    
    if (!isOpen) return null;
    
    const { cities } = content;
    const originLabel = localRoute.origin?.label || t('route.origin', 'Origin');
    
    return (
        <div className="modify-routes-overlay" onClick={onClose}>
            <div className="modify-routes-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h2>{t('route.modify_routes', 'Modify Routes')}</h2>
                    <button className="close-btn" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                {/* Route segments */}
                <div className="route-segments">
                    {/* Origin */}
                    <div className="route-segment-row origin">
                        <div className="segment-icon origin">
                            <MapPinIcon size={18} />
                        </div>
                        <div className="segment-info">
                            <span className="segment-label">{originLabel}</span>
                            <span className="segment-hint">{t('route.departure_point', 'Departure point')}</span>
                        </div>
                    </div>
                    
                    {/* Legs to each city */}
                    {cities.map((city, idx) => {
                        const leg = localRoute.legs[idx];
                        const transportType = leg?.transport?.type || 'unknown';
                        const fromLabel = idx === 0 
                            ? originLabel 
                            : cities[idx - 1].name;
                        
                        return (
                            <div key={city.id} className="route-leg-group">
                                {/* Transport selector */}
                                <div className="transport-selector">
                                    <div className="transport-route-label">
                                        <span className="from">{fromLabel}</span>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                        <span className="to">{city.name}</span>
                                    </div>
                                    <div className="transport-options">
                                        {TRANSPORT_OPTIONS.map(opt => (
                                            <button
                                                key={opt.type}
                                                className={`transport-option ${transportType === opt.type ? 'active' : ''}`}
                                                onClick={() => updateLegTransport(idx, opt.type)}
                                                title={t(opt.labelKey, opt.type)}
                                            >
                                                <TransportIcon type={opt.type} size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* City destination */}
                                <div className="route-segment-row city">
                                    <div className="segment-number">{idx + 1}</div>
                                    <div className="segment-info">
                                        <span className="segment-label">{city.name}, {city.country}</span>
                                        <span className="segment-dates">
                                            {new Date(city.dateRange.start).toLocaleDateString()} - {new Date(city.dateRange.end).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    
                    {/* Return leg */}
                    <div className="return-leg-section">
                        <label className="return-toggle">
                            <input
                                type="checkbox"
                                checked={localRoute.return?.enabled !== false}
                                onChange={e => toggleReturn(e.target.checked)}
                            />
                            <span className="toggle-switch" />
                            <span className="toggle-label">{t('route.include_return', 'Include return journey')}</span>
                        </label>
                        
                        {localRoute.return?.enabled !== false && (
                            <div className="transport-selector return">
                                <div className="transport-route-label">
                                    <span className="from">{cities[cities.length - 1]?.name || 'Destination'}</span>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M5 12h14M12 5l7 7-7 7" />
                                    </svg>
                                    <span className="to">{originLabel}</span>
                                </div>
                                <div className="transport-options">
                                    {TRANSPORT_OPTIONS.map(opt => (
                                        <button
                                            key={opt.type}
                                            className={`transport-option ${localRoute.return?.transport?.type === opt.type ? 'active' : ''}`}
                                            onClick={() => updateReturnTransport(opt.type)}
                                            title={t(opt.labelKey, opt.type)}
                                        >
                                            <TransportIcon type={opt.type} size={18} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Return destination */}
                        {localRoute.return?.enabled !== false && (
                            <div className="route-segment-row origin return">
                                <div className="segment-icon origin">
                                    <MapPinIcon size={18} />
                                </div>
                                <div className="segment-info">
                                    <span className="segment-label">{originLabel}</span>
                                    <span className="segment-hint">{t('route.return_point', 'Return point')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Error message */}
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}
                
                {/* Footer */}
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel', 'Cancel')}
                    </button>
                    <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}

